/** @jsxImportSource @emotion/react */
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { IAuthenticatorProps } from "aws-amplify-react/lib-esm/Auth/Authenticator";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input, Form, Row, Col, Typography, Button, Alert } from "antd";
import { AuthUsecase } from "../../../../usecases/Auth/usecase";
import * as Styles from "./styles";
import { AuthChallenge, AuthState } from "../const";
import { useAuthDispatch } from "../../../../usecases/Auth";
import { SEO } from "../../../../components";

interface IFormInputs {
  username: string;
  password: string;
}

const formSchema = yup.object().shape({
  username: yup
    .string()
    .email("Please enter valid email address")
    .required("Please enter email"),
  password: yup.string().required("Please enter password"),
});

const HomeBuilderSignInComponent: React.FC<IAuthenticatorProps> = ({
  onStateChange,
  authState,
}: IAuthenticatorProps) => {
  const authDispatch = useAuthDispatch();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const handleSubmitHandle = React.useCallback(
    async (data: IFormInputs) => {
      setIsProcessing(true);
      const authUsecase = new AuthUsecase(authDispatch);
      try {
        const cognitoUser = await authUsecase.signIn({
          username: data.username,
          password: data.password,
        });
        if (
          cognitoUser.challengeName === AuthChallenge.RequireNewPassword &&
          onStateChange
        ) {
          onStateChange(AuthState.RequireNewPassword, cognitoUser);
        }
        reset();
        setErrorMessage("");
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        if (error.code === "UserNotConfirmedException" && onStateChange) {
          onStateChange(AuthState.ConfirmSignUp, {
            user: {
              username: data.username,
            },
            password: data.password,
          });
          reset();
          setErrorMessage("");
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [authDispatch, onStateChange, reset],
  );

  if (authState !== AuthState.SignIn && !errorMessage) {
    return <></>;
  }

  return (
    <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
      <SEO title="Home Builder Login" />
      <Row justify="center">
        <Col css={Styles.colGutterStyle}>
          <Typography.Title level={2}>Welcome</Typography.Title>
        </Col>
      </Row>
      {errorMessage && (
        <Row justify="center">
          <Col css={Styles.colGutterStyle}>
            <Alert message={errorMessage} type="error" />
          </Col>
        </Row>
      )}
      <div css={Styles.colGutterStyle}>
        <Form.Item
          label="Email"
          css={Styles.labelStyle}
          validateStatus={errors.username ? "error" : "validating"}
          help={errors.username?.message}
        >
          <Controller
            name="username"
            control={control}
            render={({ field }) => <Input {...field} size="large" />}
          />
        </Form.Item>
      </div>
      <div css={Styles.colGutterStyle}>
        <Form.Item
          label="Password"
          css={Styles.labelStyle}
          validateStatus={errors.password ? "error" : "validating"}
          help={errors.password?.message}
        >
          <Controller
            name="password"
            control={control}
            render={({ field }) => <Input.Password {...field} size="large" />}
          />
        </Form.Item>
      </div>
      <Row>
        <Button
          type="primary"
          size="large"
          htmlType="submit"
          block
          css={Styles.colGutterStyle}
          loading={isProcessing}
        >
          Login
        </Button>
      </Row>
    </Form>
  );
};

export const HomeBuilderSignIn = React.memo(HomeBuilderSignInComponent);
